import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import type { Field as FieldDef, FieldType, ObjEntity, Severity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { useEntities, useItem } from "../../lib/data.ts";
import { KINDS } from "../../lib/itemQuery.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { usePeople } from "../../lib/people.ts";
import { atLeast } from "../../lib/roles.ts";
import { location, setQuery } from "../../lib/router.ts";
import { reflow } from "../../lib/shift.ts";
import { me } from "../../lib/state.ts";
import { useValueText } from "../../lib/textState.ts";
import { titleOf } from "../../lib/titles.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { EntityChip, EntityGlyph } from "../../ui/EntityGlyph.tsx";
import { FilterPill, TogglePill } from "../../ui/FilterPill.tsx";
import { Field, Form, Input, Select } from "../../ui/form.tsx";
import {
  AddedIcon,
  ArchivedIcon,
  BookmarkIcon,
  ClearFiltersIcon,
  ExpandIcon,
  FieldConditionIcon,
  FilterIcon,
  InFilesIcon,
  IssuesIcon,
  KIND_ICONS,
  KindFilterIcon,
  LinkIcon,
  PaperclipIcon,
  SearchIcon,
  SeenByIcon,
  UpdatedIcon,
} from "../../ui/icons.tsx";
import { LinkLine } from "../../ui/LinkIcons.tsx";
import { Menu, type MenuItem } from "../../ui/Menu.tsx";
import { FOCUS, FORM_ROW, LINK } from "../../ui/styles.ts";
import { TriageIcon } from "../../ui/TriageIcon.tsx";
import { ScalarInput } from "../item/valueInputs.tsx";
import { LevelFilter, LevelIcons } from "./levelFilter.tsx";
import { OthersPrivateToggle } from "./othersPrivate.tsx";
import { searchItems } from "./search.tsx";

/** How recently added, as durations back from now. */
const PERIODS = ["P7D", "P1M", "P3M", "P1Y"] as const;
const OPS = ["eq", "ne", "gt", "gte", "lt", "lte", "contains", "exists"] as const;
/** A location's: a point, or within a box (`lat1,lng1,lat2,lng2`). */
const LOCATION_OPS = ["eq", "ne", "within", "exists"] as const;
/**
 * Filter value editors: money and quantities compare by amount, percentages as numbers, JSON and durations by text,
 * zoned date-times by instant; emails, phones and URLs are typed as text (the server compares them by key, so any
 * way of writing one finds it).
 */
const FILTER_TYPES: Partial<Record<FieldType, FieldType>> = {
  money: "number",
  quantity: "number",
  percentage: "number",
  email: "text",
  phone: "text",
  url: "text",
  location: "text",
  any: "text",
  duration: "text",
  datetime: "instant",
};
/** Types that accept relative values (`today`, `now-PT2H`), evaluated in `tz`. */
const TEMPORAL = new Set<FieldType>(["date", "time", "datetime", "local_datetime", "instant"]);
const RELATIVE = /^(?:today|now)/u;

/** A filter value: the field's own editor, or (temporal fields) a relative value like `today+P7D`. */
function FilterValue({ field, value, onChange }: { field: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  const relative = typeof value === "string" && RELATIVE.test(value);
  return (
    // The value as wide as the panel (beside Today and Now, for dates).
    <div class={`${FORM_ROW} [&>:first-child]:min-w-0 [&>:first-child]:flex-1`}>
      <Field label={t("items.value")} hint={relative ? t("items.relativeHint") : null}>
        {relative ? (
          <Input value={value} onValue={onChange} spellcheck={false} />
        ) : (
          <ScalarInput key={field.key} field={field} value={value} onChange={onChange} />
        )}
      </Field>
      {TEMPORAL.has(field.type) ? (
        <>
          <Button onClick={() => onChange("today")}>{t("items.today")}</Button>
          <Button onClick={() => onChange("now")}>{t("items.now")}</Button>
        </>
      ) : null}
    </div>
  );
}

/** A field condition (`path:op:value`, as `where` takes it), split; none when it isn't one. */
function splitCondition(w: string): { path: string; op: string; value: string } | null {
  const [path, op, ...rest] = w.split(":");
  return path && op ? { path, op, value: rest.join(":") } : null;
}

/**
 * A field condition: the field, how it compares and the value; `initial` (one already set) to change it. `onDone` gets
 * it as `where` takes it.
 */
function ConditionBuilder({
  entity,
  initial,
  onDone,
}: {
  entity: ObjEntity | undefined;
  initial?: string | undefined;
  onDone: (where: string) => void;
}) {
  const fields = (entity?.fields ?? []).filter((x) => x.type !== "ref");
  const was = initial ? splitCondition(initial) : null;
  const [path, setPath] = useState(was?.path ?? "title");
  const [op, setOp] = useState<string>(was?.op ?? "contains");
  const [value, setValue] = useState<unknown>(was && was.op !== "exists" ? was.value : "");
  const f = fields.find((x) => `data.${x.key}` === path);
  const field: FieldDef = f
    ? {
        key: f.key,
        type: FILTER_TYPES[f.type] ?? f.type,
        options: f.options ?? [],
        ...(f.labels && { labels: f.labels }),
      }
    : { key: "title", type: "text" };
  const paths = [
    { value: "title", label: t("items.title") },
    ...fields.map((x) => ({ value: `data.${x.key}`, label: x.label || x.key })),
  ];
  return (
    <Form
      class="flex flex-col gap-3"
      onSubmit={() => onDone(`${path}:${op}:${op === "exists" ? "true" : String(value ?? "")}`)}
    >
      <Field label={t("items.filterField")}>
        <Select
          value={path}
          onValue={(p) => {
            setPath(p);
            setValue("");
          }}
          options={paths}
        />
      </Field>
      <Field label={t("items.operator")}>
        <Select
          value={op}
          onValue={setOp}
          options={(f?.type === "location" ? LOCATION_OPS : OPS).map((o) => ({ value: o, label: t(`op.${o}`) }))}
        />
      </Field>
      {op === "exists" ? null : <FilterValue field={field} value={value} onChange={setValue} />}
      <Button submit={true} variant="primary">
        {initial ? t("items.filter.apply") : t("items.addFilter")}
      </Button>
    </Form>
  );
}

/** A field condition as a pill says it: the field's name, then how it compares and to what. */
function conditionText(w: string, entity: ObjEntity | undefined): { name: string; value: string } {
  const c = splitCondition(w);
  if (!c) {
    return { name: w, value: "" };
  }
  const key = c.path.startsWith("data.") ? c.path.slice("data.".length) : null;
  const f = key ? entity?.fields?.find((x) => x.key === key) : undefined;
  const name = key ? (f?.label ?? key) : t("items.title");
  const shown = f?.labels?.[c.value] ?? c.value;
  return {
    name,
    value:
      c.op === "exists"
        ? t("op.exists")
        : `${t(`op.${c.op as (typeof OPS)[number] | (typeof LOCATION_OPS)[number]}`)} ${shown}`,
  };
}

/** A way a listed item can be linked: which condition picking an item adds, and what that item is searched among. */
interface Relation {
  value: string;
  label: string;
  /** The condition, `refers_to` (the listed item links to it) or `linked_from` (it links to the listed item). */
  param: "refers_to" | "linked_from";
  /** Through this ref field (none: any). */
  key: string | null;
  /** The entity the item is searched in (none: all items): its icon and color are the pill's. */
  entity: string | null;
  /** Through a field that may link to any item, or any link (none of them name the entity at the other end). */
  any: boolean;
  /** Another entity's field pointing at the listed one (said after that entity's tag in Filters, as the map draws it). */
  field?: FieldDef;
}

/** The listed entity's ref fields, other entities' ref fields that may point to it, and links of any field. */
function relations(entity: ObjEntity | undefined, entities: readonly ObjEntity[]): Relation[] {
  const own: Relation[] = (entity?.fields ?? [])
    .filter((f) => f.type === "ref")
    .map((f) => ({
      value: `to:${f.key}`,
      label: f.label || f.key,
      param: "refers_to",
      key: f.key,
      entity: f.target ?? null,
      any: !f.target,
    }));
  const theirs: Relation[] = entity
    ? entities.flatMap((e) =>
        (e.fields ?? [])
          // Not its own fields again (its items linking among themselves): listed above, the way they link.
          .filter((f) => f.type === "ref" && (!f.target || f.target === entity.id) && e.id !== entity.id)
          .map((f) => ({
            value: `from:${e.id}:${f.key}`,
            label: t("items.related.inOf", { entity: e.name, field: f.label || f.key }),
            param: "linked_from" as const,
            key: f.key,
            entity: e.id,
            any: !f.target,
            field: f,
          })),
      )
    : [];
  // Those naming the entity at the other end first; then those through fields that may link to any item, and any link.
  return [
    ...[...own, ...theirs].filter((r) => !r.any),
    ...[...own, ...theirs].filter((r) => r.any),
    { value: "to", label: t("items.related.linksTo"), param: "refers_to", key: null, entity: null, any: true },
    { value: "from", label: t("items.related.linkedFrom"), param: "linked_from", key: null, entity: null, any: true },
  ];
}

/** The reference conditions a list takes, each repeatable. */
const LINKS = ["refers_to", "linked_from"] as const;

/** A link condition's value: through a field (`key:id`) or any (`id`). Ids have no colon: the field is before the last. */
function splitLink(value: string): { key: string | null; id: string } {
  const at = value.lastIndexOf(":");
  return { key: at < 0 ? null : value.slice(0, at), id: value.slice(at + 1) };
}

/** The relation's icon: its entity's, in its color; a link without one. */
function RelationIcon({ entity }: { entity: ObjEntity | undefined }) {
  return entity?.icon ? <EntityGlyph icon={entity.icon} size={14} /> : <LinkIcon size={14} />;
}

/**
 * Another entity's field pointing at the listed one, as the map draws a link: a line from here (many at this end if the
 * field takes many, at its holder's unless it's unique; a diamond at the whole's end), the field, then the entity it's
 * of (its tag). A field named as the listed entity (`here`) or its own is left unsaid: the line and tag say it; the
 * listed entity's own (a link among its items), its tag is.
 */
function TheirField({ entity, field, here }: { entity: ObjEntity; field: FieldDef; here: ObjEntity | undefined }) {
  const name = field.label || field.key;
  const same = (other: string | undefined) => other?.trim().toLocaleLowerCase() === name.trim().toLocaleLowerCase();
  const self = entity.id === here?.id;
  return (
    <span class="flex min-w-0 items-center gap-2">
      {/* As the menu's icons are. */}
      <span class="flex shrink-0 text-zinc-500">
        <LinkLine
          many={[field.many ?? false, !field.unique]}
          whole={field.ownership === "contains" ? "end" : field.ownership === "part_of" ? "start" : null}
        />
      </span>
      <span class="flex min-w-0 items-center gap-1.5">
        {!self && (same(here?.name) || same(entity.name)) ? null : <span class="truncate">{name}</span>}
        {self ? null : <EntityChip entity={entity} />}
      </span>
    </span>
  );
}

/** Picking the item of a link condition: searched among its relation's entity. */
function ItemPicker(p: { space: SpaceOut; relation: Relation; onPick: (id: string) => void }) {
  return (
    <Combobox
      label={t("items.related.item")}
      placeholder={t("common.searchItems")}
      search={(q, signal) => searchItems(p.space, q, signal, { entity: p.relation.entity })}
      onSelect={(o) => p.onPick(o.value)}
      inline={true}
    />
  );
}

/** Adds (or, at `index`, replaces) a link condition of `relation`'s with item `id`. */
function putLink(relation: Relation, id: string, index?: number): void {
  const values = location.value.searchParams.getAll(relation.param);
  const value = relation.key ? `${relation.key}:${id}` : id;
  const next = index === undefined ? [...values, value] : values.map((v, i) => (i === index ? value : v));
  filterBy({ [relation.param]: next, cursor: null });
}

/** One link condition set: its relation's pill (its entity's icon and tint), the item it names; changed in its panel. */
function LinkPill(p: {
  space: SpaceOut;
  entity: ObjEntity | undefined;
  entities: readonly ObjEntity[];
  param: (typeof LINKS)[number];
  values: readonly string[];
  index: number;
}) {
  const { key, id } = splitLink(p.values[p.index] ?? "");
  const item = useItem(p.space, id);
  const title = item.data ? titleOf(item.data) : "…";
  const all = relations(p.entity, p.entities);
  // Its relation: by field (and, for another entity's field, the item's entity); else any link that way.
  const relation =
    all.find(
      (r) =>
        r.param === p.param &&
        r.key === key &&
        (p.param === "refers_to" || key === null || r.entity === (item.data?.entity ?? r.entity)),
    ) ??
    ({
      value: p.param,
      label: key ?? t(p.param === "refers_to" ? "items.related.linksTo" : "items.related.linkedFrom"),
      param: p.param,
      key,
      entity: null,
      any: true,
    } satisfies Relation);
  const shown = p.entities.find((e) => e.id === (relation.entity ?? item.data?.entity));
  return (
    <FilterPill
      icon={<RelationIcon entity={shown} />}
      name={relation.label}
      label={relation.field && shown ? <TheirField entity={shown} field={relation.field} here={p.entity} /> : undefined}
      value={title}
      tint={shown?.icon?.background ?? null}
      wide={true}
      named={true}
      onClear={() => filterBy({ [p.param]: p.values.filter((_, j) => j !== p.index), cursor: null })}
    >
      {(close) => (
        <div class="flex flex-col gap-3">
          <Link href={spaceUrl(p.space, `/items/${id}`)} class={LINK}>
            {t("items.related.open", { title })}
          </Link>
          <ItemPicker
            space={p.space}
            relation={relation}
            onPick={(next) => {
              putLink(relation, next, p.index);
              close();
            }}
          />
        </div>
      )}
    </FilterPill>
  );
}

/** A filter of a few choices, each its URL value, as a pill. */
interface Simple {
  param: string;
  icon: ComponentChildren;
  name: string;
  /** `pill`: how the pill says it, when the choice alone doesn't say what it's of. */
  options: readonly { value: string; label: string; pill?: string; icon?: ComponentChildren }[];
}

const EmailIcon = KIND_ICONS.email;

/** A filter of one choice is on or off: a toggle, not a menu. */
const isToggle = (f: Simple) => f.options.length === 1;

/** The pill of a filter of a few choices: set, what it's set to; its panel, the choices. */
function SimplePill(p: { f: Simple; open?: boolean }) {
  const current = location.value.searchParams.get(p.f.param);
  const picked = p.f.options.find((o) => o.value === current);
  const on = p.f.options[0]?.value ?? "true";
  if (isToggle(p.f)) {
    return (
      <TogglePill
        icon={p.f.icon}
        name={p.f.name}
        pressed={current === on}
        onToggle={() => filterBy({ [p.f.param]: current === on ? null : on, cursor: null })}
      />
    );
  }
  return (
    <FilterPill
      icon={picked?.icon ?? p.f.icon}
      name={p.f.name}
      value={picked?.pill ?? picked?.label}
      onClear={() => filterBy({ [p.f.param]: null, cursor: null })}
      open={p.open ?? false}
      items={p.f.options.map((o) => ({
        key: o.value,
        label: o.label,
        icon: o.icon,
        current: o.value === current,
        onSelect: () => filterBy({ [p.f.param]: o.value === current ? null : o.value, cursor: null }),
      }))}
    />
  );
}

/** The filters of a few choices: Archived, Kind and Date added always shown, the rest once set (or added with Filters). */
function simpleFilters(): Simple[] {
  return [
    {
      param: "archived",
      icon: <ArchivedIcon size={14} />,
      name: t("items.archived"),
      options: [{ value: "true", label: t("items.archived") }],
    },
    {
      param: "kind",
      icon: <KindFilterIcon size={14} />,
      name: t("items.kind"),
      options: KINDS.map((k) => {
        const Glyph = KIND_ICONS[k];
        return { value: k, label: t(`items.kindFilter.${k}`), icon: <Glyph size={14} /> };
      }),
    },
    {
      param: "since",
      icon: <AddedIcon size={14} />,
      name: t("items.since"),
      options: PERIODS.map((p) => ({ value: p, label: t(`items.since.${p}`), pill: t(`items.added.${p}`) })),
    },
    {
      param: "updated_since",
      icon: <UpdatedIcon size={14} />,
      name: t("items.updatedSince"),
      options: PERIODS.map((p) => ({ value: p, label: t(`items.since.${p}`), pill: t(`items.updated.${p}`) })),
    },
    {
      param: "attachments",
      icon: <PaperclipIcon size={14} />,
      name: t("items.attachments"),
      options: [
        { value: "true", label: t("items.withAttachments") },
        { value: "false", label: t("items.withoutAttachments") },
      ],
    },
    {
      param: "triage",
      icon: <TriageIcon size={14} />,
      name: t("items.triage"),
      options: [
        { value: "true", label: t("items.inTriage") },
        { value: "false", label: t("items.outOfTriage") },
      ],
    },
    {
      param: "attached",
      icon: <PaperclipIcon size={14} />,
      name: t("items.attached"),
      options: [
        { value: "false", label: t("items.attachedHide") },
        { value: "true", label: t("items.attachedOnly") },
      ],
    },
    {
      param: "bookmarked",
      icon: <BookmarkIcon size={14} />,
      name: t("items.bookmarked"),
      options: [
        { value: "true", label: t("items.bookmarkedOnly") },
        { value: "false", label: t("items.bookmarkedNot") },
      ],
    },
    {
      param: "in_files",
      icon: <InFilesIcon size={14} />,
      name: t("items.inFiles"),
      options: [
        { value: "include", label: t("items.inFilesInclude") },
        { value: "only", label: t("items.inFilesOnly") },
      ],
    },
  ];
}

/** Filters and Clear: pills' shape and height (a border's room kept), no border. */
const BARE_PILL = `inline-flex items-center gap-1 rounded-full border border-transparent px-2.5 py-1 text-sm text-zinc-600 hover:bg-zinc-950/5 hover:text-zinc-900 ${FOCUS}`;

/** Always shown, set or not. */
const ALWAYS = new Set(["kind", "since", "archived"]);

/** Open issues of at least a level, as a pill. */
function IssuesPill() {
  const level = (location.value.searchParams.get("issues") ?? "") as Severity | "";
  return (
    <FilterPill
      icon={level ? <LevelIcons level={level} size={14} /> : <IssuesIcon size={14} />}
      name={t("items.issues")}
      value={level ? t(`items.atLeast.${level}`) : null}
      onClear={() => filterBy({ issues: null, cursor: null })}
    >
      {(close) => (
        <LevelFilter
          label={t("items.issues")}
          value={level}
          onPick={(v) => {
            filterBy({ issues: v === level ? null : v || null, cursor: null });
            close();
          }}
          name={(s) => t(`items.atLeast.${s}`)}
          all={{ label: t("items.issuesAll"), title: t("items.anyIssues") }}
        />
      )}
    </FilterPill>
  );
}

/** What each of the visibility menu's choices asks the list for (the rest of these left out). */
const SHARING: Readonly<Record<string, { visibility?: string; guests?: string; private_to?: string }>> = {
  org: { visibility: "org" },
  public: { visibility: "public" },
  guests: { guests: "true" },
  me: { private_to: "me" },
};

/**
 * Who sees them: everything you see (the default); how they're shared (with the organization, publicly by link, with
 * guests); or private ones: yours, or (admins) one member's you see (all of them, with others' private items shown).
 */
function VisibilityPill(p: { space: SpaceOut; open?: boolean }) {
  const members = usePeople();
  const admin = atLeast(p.space.role, "admin");
  const sp = location.value.searchParams;
  // Which of its choices the list's filters are (as the menu sets them).
  const current = (sp.get("guests") === "true" ? "guests" : sp.get("visibility")) ?? sp.get("private_to") ?? "";
  // Shared (how far), then private (whose): the pill says it whole, its group and choice.
  const shared = t("visibility.filterShared");
  const own = t("visibility.filterPrivate");
  const options = [
    { value: "org", label: t("visibility.filterOrg"), pill: t("visibility.pillOrg"), group: shared },
    { value: "public", label: t("visibility.filterPublic"), pill: t("visibility.pillPublic"), group: shared },
    { value: "guests", label: t("visibility.filterGuests"), pill: t("visibility.pillGuests"), group: shared },
    { value: "me", label: t("visibility.filterMine"), pill: t("visibility.pillMine"), group: own },
    ...(admin
      ? (members ?? [])
          .filter((m) => m.id !== me.value?.id)
          .map((m) => ({ value: m.id, label: m.name, pill: t("visibility.pillOf", { name: m.name }), group: own }))
      : []),
  ];
  const pick = (v: string) => {
    const asked = SHARING[v] ?? (v ? { private_to: v } : {});
    filterBy({
      visibility: asked.visibility ?? null,
      guests: asked.guests ?? null,
      private_to: asked.private_to ?? null,
      cursor: null,
    });
  };
  return (
    <FilterPill
      icon={<SeenByIcon size={14} />}
      name={t("visibility.label")}
      value={options.find((o) => o.value === current)?.pill}
      onClear={() => pick("")}
      open={p.open ?? false}
      items={[
        ...options.map((o) => ({
          key: o.value,
          label: o.label,
          group: o.group,
          current: o.value === current,
          onSelect: () => pick(o.value === current ? "" : o.value),
        })),
        { key: "others-private", content: () => <OthersPrivateToggle space={p.space} />, separated: true },
      ]}
    />
  );
}

/** Whether the visibility filter is set (any of the params it sets). */
const visibilitySet = (sp: URLSearchParams) => ["visibility", "guests", "private_to"].some((k) => sp.get(k));

/** Everything a list can be filtered by beyond search, cleared at once by Clear. */
const CLEARED = [
  "kind",
  "since",
  "updated_since",
  "issues",
  "attachments",
  "triage",
  "private_to",
  "visibility",
  "guests",
  "attached",
  "archived",
  "bookmarked",
  "in_comments",
  "in_files",
  "thread",
  "where",
  ...LINKS,
] as const;

/** How long typing pauses before the list searches for it, ms. */
const SEARCH_PAUSE_MS = 300;

/** Full-text search as you type (once typing pauses); the list's search when it changes (back, a link). */
function SearchBox() {
  const asked = location.value.searchParams.get("q") ?? "";
  const [q, setQ] = useValueText(
    asked,
    (v) => v,
    (text, v) => text === v,
  );
  useEffect(() => {
    if (q.trim() === asked.trim()) {
      return;
    }
    const later = setTimeout(() => setQuery({ q: q.trim() || null, cursor: null }), SEARCH_PAUSE_MS);
    return () => clearTimeout(later);
  }, [q, asked]);
  return (
    <Form class="relative flex-1" onSubmit={() => setQuery({ q: q.trim() || null, cursor: null })}>
      <SearchIcon size={16} class="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-zinc-400" />
      <Input
        type="search"
        aria-label={t("items.search")}
        placeholder={t("items.searchPlaceholder")}
        value={q}
        onValue={setQ}
        class="pl-8"
      />
    </Form>
  );
}

/** The list filtered as `query` says, each pill moving to where it is then in the iOS app (lib/shift.ts). */
function filterBy(query: Parameters<typeof setQuery>[0]): void {
  reflow("pill", () => setQuery(query));
}

/** A pill in the filters' row, marked for its view transition (`filterBy`). */
function Pill(p: { name: string; children: ComponentChildren }) {
  return (
    <span data-pill={p.name} class="flex max-w-full">
      {p.children}
    </span>
  );
}

/**
 * The list's filters as pills (Airbnb-style), under the search: kind, when added and issues always, then every other filter
 * set, each field condition and each link condition (in its entity's icon and tint); Filters adds one, its pill
 * open to set it. Each pill's panel sets it; its × takes it off; Clear takes them all off.
 */
export function Filters({
  space,
  entity,
  inFolder = false,
}: {
  space: SpaceOut;
  entity: ObjEntity | undefined;
  /** In a folder: the page is headed by it, not the sidebar's entity, which is then said here too. */
  inFolder?: boolean;
}) {
  const sp = location.value.searchParams;
  const entities = useEntities(space).data ?? [];
  // The filter last added from Filters: open to be set as it comes, and kept out here (set or not) until another is.
  const [adding, setAdding] = useState<string | null>(null);
  const simple = simpleFilters();
  const wheres = sp.getAll("where");
  const rels = relations(entity, entities);
  const shown = simple.filter((f) => ALWAYS.has(f.param) || sp.get(f.param) || adding === f.param);
  const active = CLEARED.some((k) => sp.getAll(k).length > 0);
  const done = () => setAdding(null);
  const menu: MenuItem[] = [
    ...simple
      .filter((f) => !(ALWAYS.has(f.param) || sp.get(f.param) || adding === f.param))
      .map((f) => ({
        key: f.param,
        label: f.name,
        icon: f.icon,
        onSelect: () => {
          // A toggle's turned on as it's added (its one choice); the others open to be set.
          if (isToggle(f)) {
            filterBy({ [f.param]: f.options[0]?.value ?? null, cursor: null });
          }
          setAdding(f.param);
        },
      })),
    ...(visibilitySet(sp) || adding === "visibility"
      ? []
      : [
          {
            key: "visibility",
            label: t("visibility.label"),
            icon: <SeenByIcon size={14} />,
            onSelect: () => setAdding("visibility"),
          },
        ]),
    {
      key: "where",
      label: t("items.filter.field"),
      icon: <FieldConditionIcon size={14} />,
      onSelect: () => setAdding("where"),
    },
    ...rels.map((r) => {
      const e = entities.find((x) => x.id === r.entity);
      return {
        key: `rel:${r.value}`,
        label: r.label,
        icon: <RelationIcon entity={e} />,
        // Another entity's field: the link drawn (where the others' icons are), its field, the entity it's of.
        ...(r.field && e
          ? {
              display: <TheirField entity={e} field={r.field} here={entity} />,
            }
          : {}),
        group: t(r.any ? "items.filter.linksAny" : "items.filter.links"),
        onSelect: () => setAdding(`rel:${r.value}`),
      };
    }),
  ];
  const addingRelation = adding?.startsWith("rel:") ? rels.find((r) => `rel:${r.value}` === adding) : undefined;
  return (
    <search class="mb-4 flex flex-col gap-2">
      <SearchBox />
      <div class="flex flex-wrap items-center gap-2">
        {shown.map((f) => (
          <Pill key={f.param} name={f.param}>
            <SimplePill f={f} open={adding === f.param && !isToggle(f)} />
          </Pill>
        ))}
        <Pill name="issues">
          <IssuesPill />
        </Pill>
        {/* The sidebar's entity, in a folder (its name isn't the heading there): said as a filter, changed or taken off. */}
        {inFolder && sp.get("entity") ? (
          <Pill name="entity">
            <SimplePill
              f={{
                param: "entity",
                icon: <EntityGlyph icon={entity?.icon ?? null} size={14} empty={entity ? "dot" : "none"} />,
                name: t("items.entity"),
                options: [
                  ...entities.map((e) => ({
                    value: e.id,
                    label: e.name,
                    icon: <EntityGlyph icon={e.icon ?? null} size={14} empty="dot" />,
                  })),
                  {
                    value: "none",
                    label: t("items.noEntity"),
                    icon: <EntityGlyph icon={null} size={14} empty="none" />,
                  },
                ],
              }}
            />
          </Pill>
        ) : null}
        {/* One conversation's emails (opened from its group in the tree): only taken off. */}
        {sp.get("thread") ? (
          <Pill name="thread">
            <TogglePill
              icon={<EmailIcon size={14} />}
              name={t("items.tree.thread")}
              pressed={true}
              onToggle={() => filterBy({ thread: null, cursor: null })}
            />
          </Pill>
        ) : null}
        {visibilitySet(sp) || adding === "visibility" ? (
          <Pill name="visibility">
            <VisibilityPill space={space} open={adding === "visibility"} />
          </Pill>
        ) : null}
        {wheres.map((w, i) => {
          const c = conditionText(w, entity);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: the same condition can be there twice.
            <Pill key={`${i}:${w}`} name={`where:${i}:${w}`}>
              <FilterPill
                icon={<FieldConditionIcon size={14} />}
                name={c.name}
                value={c.value}
                wide={true}
                form={true}
                named={true}
                onClear={() => filterBy({ where: wheres.filter((_, j) => j !== i), cursor: null })}
              >
                {(close) => (
                  <ConditionBuilder
                    entity={entity}
                    initial={w}
                    onDone={(next) => {
                      filterBy({ where: wheres.map((x, j) => (j === i ? next : x)), cursor: null });
                      close();
                    }}
                  />
                )}
              </FilterPill>
            </Pill>
          );
        })}
        {adding === "where" ? (
          <Pill name="where-new">
            <FilterPill
              icon={<FieldConditionIcon size={14} />}
              name={t("items.filter.field")}
              wide={true}
              form={true}
              named={true}
              open={true}
            >
              {(close) => (
                <ConditionBuilder
                  entity={entity}
                  onDone={(next) => {
                    filterBy({ where: [...wheres, next], cursor: null });
                    done();
                    close();
                  }}
                />
              )}
            </FilterPill>
          </Pill>
        ) : null}
        {LINKS.flatMap((param) => {
          const values = sp.getAll(param);
          return values.map((v, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: the same condition can be there twice.
            <Pill key={`${param}:${index}:${v}`} name={`${param}:${index}:${v}`}>
              <LinkPill {...{ space, entity, entities, param, values, index }} />
            </Pill>
          ));
        })}
        {addingRelation ? (
          <Pill name="rel-new">
            <FilterPill
              icon={<RelationIcon entity={entities.find((e) => e.id === addingRelation.entity)} />}
              name={addingRelation.label}
              label={(() => {
                const e = entities.find((x) => x.id === addingRelation.entity);
                return addingRelation.field && e ? (
                  <TheirField entity={e} field={addingRelation.field} here={entity} />
                ) : undefined;
              })()}
              wide={true}
              named={true}
              open={true}
            >
              {(close) => (
                <ItemPicker
                  space={space}
                  relation={addingRelation}
                  onPick={(id) => {
                    putLink(addingRelation, id);
                    done();
                    close();
                  }}
                />
              )}
            </FilterPill>
          </Pill>
        ) : null}
        <Pill name="add">
          <Menu
            label={
              <>
                <FilterIcon size={14} />
                {t("items.filter.add")}
                <ExpandIcon size={14} class="opacity-60" />
              </>
            }
            aria-label={t("items.filter.addLabel")}
            class={BARE_PILL}
            items={menu}
          />
        </Pill>
        {active ? (
          <Pill name="clear">
            <button
              type="button"
              class={BARE_PILL}
              onClick={() => filterBy({ ...Object.fromEntries(CLEARED.map((k) => [k, null])), cursor: null })}
            >
              <ClearFiltersIcon size={14} />
              {t("items.clearFilters")}
            </button>
          </Pill>
        ) : null}
      </div>
    </search>
  );
}
